import { useEffect, useState } from "react";
import type { KeyboardEvent } from "react";
import { Button, Form, Input, message } from "antd";
import {
  SafetyOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  MailOutlined,
  ShopOutlined,
  UserOutlined,
  PhoneOutlined,
  IdcardOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import TextField from "app/shared/TextField";
import FinkraftLogo from "app/assets/images/finkraftlogo.png";
import { getSubdomain } from "app/utils/generic.helper";
import { appMetaConfig } from "global.constants";
import "./LoginCleartrip.scss";
import { LogoutSharp } from "@mui/icons-material";
import SaintGobainLogo from "static/images/SaintGobain.png";
import SkodaLogo from "static/images/skoda.jpeg";
import JswLogo from "static/images/JSW.webp";
import BcgLogo from "static/images/BCG.jpg";
import CleartripLogo from "static/images/Cleartrip_Original.svg.webp";
import SunPharmaLogo from "static/images/sun-pharma-logo-png.png";
import YatraLogo from "static/images/Yatra_logo.svg.webp";
import AtpiLogo from "static/images/atpi-ltd-logo-vector.svg";
import MmtLogo from "static/images/mmtNew.png";
import ZiptrripLogo from "static/images/ZipTrripLogo.png";
import FcmLogo from "static/images/fcmimages.png";
import BcdLogo from "static/images/bcdTravel.png";
import TravelPlusLogo from "static/images/TravelPlus.png";
import PytLogo from "static/images/pytimages.png";
import EbixLogo from "static/images/ebixNew.png";
import SamsungLogo from "static/images/samsung.jpg";
import EmtLogo from "static/images/emtimages.jpeg";
import Quest2TravelLogo from "static/images/q2Travel.png";
import BalmerLogo from "static/images/balmer.png";

const LOCAL_DOMAIN_LOGOS: Record<string, string> = {
  cleartrip: CleartripLogo,
  sunpharma: SunPharmaLogo,
  myyatra: YatraLogo,
  atpi: AtpiLogo,
  mmt: MmtLogo,
  ziptrrip: ZiptrripLogo,
  fcm: FcmLogo,
  bcd: BcdLogo,
  travelplus: TravelPlusLogo,
  pyt: PytLogo,
  ebix: EbixLogo,
  samsung: SamsungLogo,
  emt: EmtLogo,
  quest2travel: Quest2TravelLogo,
  balmer: BalmerLogo,
};

// Fine-tune the target width (see handleLogoLoad) for specific domains whose
// logo reads too wide/narrow at the generic default.
const LOGO_TARGET_WIDTH: Record<string, number> = {
  atpi: 120,
  mmt: 140,
  fcm: 100,
};

// Fine-tune the footer "× finkraft.ai" brand-lockup logo height for specific
// domains whose logo reads too big/small at the default 40px.
const FOOTER_SEPARATOR_MARGIN_LEFT: Record<string, number> = {
  cleartrip: -8,
};

const FOOTER_SEPARATOR_MARGIN_RIGHT: Record<string, number> = {
  cleartrip: 8,
};

const FOOTER_LOGO_HEIGHT: Record<string, number> = {
  ziptrrip: 26,
  samsung: 22,
};

const API_INVOICE_UPLOAD_CONFIG = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/invoice-upload-config";
const API_SEND_OTP = "https://stage-apiplatform.finkraftai.com/api/login/hotel/loginotp";
const API_VERIFY_OTP = "https://stage-apiplatform.finkraftai.com/api/login/hotel/verifyOTP";

const TRAVEL_PARTNER_KEYS = [
  "mmt",
  "pyt",
  "balmer",
  "myyatra",
  "samsung",
  "sunpharma",
  "ebix",
  "ziptrrip",
  "travelplus",
  "solutionstravel",
  "quest2travel",
  "emt",
  "tripmartz",
];

const CORPORATE_CLIENT_LOGOS = [
  { key: "saintgobain", src: SaintGobainLogo, alt: "Saint-Gobain" },
  { key: "skoda", src: SkodaLogo, alt: "Skoda" },
  { key: "jsw", src: JswLogo, alt: "JSW" },
  { key: "bcg", src: BcgLogo, alt: "BCG" },
];

const FEATURES = [
  { icon: <CheckCircleOutlined />, text: "Upload invoices for all bookings in one place" },
  { icon: <CheckCircleOutlined />, text: "Track your compliance score in real time" },
  { icon: <CheckCircleOutlined />, text: "Secure GSTIN-verified hotel identity" },
];

export default function 
LoginCleartrip() {
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [uploadConfig, setUploadConfig] = useState<any>(null);
  const [messageApi, contextHolder] = message.useMessage();
  const domain = getSubdomain();
  const [step, setStep] = useState<"details" | "otp">("details");
  const [pendingValues, setPendingValues] = useState<any>(null);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    const fetchUploadConfig = async () => {
      try {
        const res = await fetch(`${API_INVOICE_UPLOAD_CONFIG}?domain=${domain}`);
        const response = await res.json();
        setUploadConfig(response?.data ?? response);
      } catch (error) {
        setUploadConfig(null);
      }
    };
    fetchUploadConfig();
  }, []);

  const accentColor = uploadConfig?.find((c: any) => c.color)?.color;
  const logoUrl =
    LOCAL_DOMAIN_LOGOS[domain] || uploadConfig?.find((c: any) => c.logo)?.logo;
  const [logoBoxHeight, setLogoBoxHeight] = useState(48);
  const partnerName = appMetaConfig[domain]?.title?.replace(/\s*-+\s*Finkraft$/i, "").trim();

  function handleLogoLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    if (!naturalWidth || !naturalHeight) return;
    // Target a consistent visual width and derive the height from each logo's
    // own aspect ratio, instead of a fixed height that only suits one shape
    // (a wide wordmark looks fine at a short height, but a square/portrait
    // logo needs to be taller to read at the same width).
    const aspectRatio = naturalWidth / naturalHeight;
    const targetWidth = LOGO_TARGET_WIDTH[domain] ?? 160;
    const height = Math.min(110, Math.max(40, targetWidth / aspectRatio));
    setLogoBoxHeight(Math.round(height));
  }

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const res = await fetch(API_SEND_OTP, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: values.email,
          name: values.contactName,
          mobile_no: values.mobile,
          hotel_name: values.vendorName,
          hotel_gstin: values.gstin,
        }),
      });
      const result = await res.json().catch(() => null);
      if (res.ok && result?.status !== false) {
        setPendingValues(values);
        setStep("otp");
        messageApi.success(`OTP sent to ${values.email}`);
      } else {
        messageApi.error(result?.message || "Failed to send OTP. Please try again.");
      }
    } catch (error) {
      messageApi.error("Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 6) {
      messageApi.error("Please enter the 6-digit OTP.");
      return;
    }
    setOtpLoading(true);
    try {
      const res = await fetch(API_VERIFY_OTP, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: pendingValues.email, otp }),
      });
      const result = await res.json().catch(() => null);
      if (res.ok && result?.status !== false) {
        if (result?.data?.token) localStorage.setItem("token", result.data.token);
        localStorage.setItem("hotelGstin", pendingValues.gstin);
        localStorage.setItem("hotelName", pendingValues.vendorName);
        messageApi.success("Signed in successfully");
        navigate("/hotelInvoices/table");
      } else {
        messageApi.error(result?.message || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      messageApi.error("Failed to verify OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!pendingValues) return;
    setResending(true);
    try {
      const res = await fetch(API_SEND_OTP, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: pendingValues.email,
          name: pendingValues.contactName,
          mobile_no: pendingValues.mobile,
          hotel_name: pendingValues.vendorName,
          hotel_gstin: pendingValues.gstin,
        }),
      });
      const result = await res.json().catch(() => null);
      if (res.ok && result?.status !== false) {
        messageApi.success(`OTP resent to ${pendingValues.email}`);
      } else {
        messageApi.error(result?.message || "Failed to resend OTP.");
      }
    } catch (error) {
      messageApi.error("Failed to resend OTP.");
    } finally {
      setResending(false);
    }
  };

  const handleBackToDetails = () => {
    setStep("details");
    setOtp("");
  };

  return (
    <div className="LoginCleartrip" style={{ "--accent-color": accentColor } as React.CSSProperties}>
      {contextHolder}
      <div className="LeftPanel">
        {logoUrl && (
          <div
            className={
              domain === "app" || LOCAL_DOMAIN_LOGOS[domain] ? "Logo Logo--plain" : "Logo"
            }
            style={{ height: logoBoxHeight }}
          >
            <img src={logoUrl} alt="" onLoad={handleLogoLoad} />
          </div>
        )}

        <div className="LeftContent">
          {domain === "app" && (
            <div className="TravelPartners">
              <div className="TravelPartnersLabel">OUR TRAVEL PARTNERS</div>
              <div className="TravelPartnersList">
                <div className="TravelPartnersTrack">
                  {[...TRAVEL_PARTNER_KEYS, ...TRAVEL_PARTNER_KEYS].map((key, index) => (
                    <div className="PartnerLogoChip" key={`${key}-${index}`}>
                      <img src={appMetaConfig[key]?.logo} alt={appMetaConfig[key]?.title} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {domain === "app" && (
            <div className="CorporateClients">
              <div className="CorporateClientsLabel">OUR CORPORATE CLIENTS</div>
              <div className="CorporateClientsList">
                <div className="CorporateClientsTrack">
                  {[...CORPORATE_CLIENT_LOGOS, ...CORPORATE_CLIENT_LOGOS].map((logo, index) => (
                    <div className="PartnerLogoChip" data-key={logo.key} key={`${logo.key}-${index}`}>
                      <img src={logo.src} alt={logo.alt} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="PitchSection">
            <div className="SectionBadge">
              <SafetyOutlined /> Hotel Compliance Portal
            </div>
            <div className="Headline">
              Simplifying hotel invoice
              <span className="accent">compliance at scale.</span>
            </div>
            <div className="SubText">
              A seamless portal for{" "}
              {domain !== "app" && partnerName ? `${partnerName} partner ` : ""}
              hotels to submit GST invoices, track compliance, and build a
              lasting business relationship.
            </div>
          </div>

          <div className="FeatureList">
            {FEATURES.map((feature) => (
              <div className="FeatureItem" key={feature.text}>
                <div className="FeatureIcon">{feature.icon}</div>
                {feature.text}
              </div>
            ))}
          </div>
        </div>

        <div className="PoweredBy">
          Powered by <img src={FinkraftLogo} alt="finkraft.ai" />
        </div>
      </div>

      <div className="RightPanel">
        <div className="RightPanelInner">
          <div className="LoginCard">
          <div className="PortalBadge">{domain === "cleartrip" && "CLEARTRIP · "}HOTEL GST INVOICE HUB</div>

          <div className="CardTitle">{step === "otp" ? "Verify Your Email" : "Hotel Partner Sign In"}</div>
          <div className="CardSubtitle">
            {step === "otp"
              ? `We've sent a 6-digit OTP to ${pendingValues?.email}.`
              : "Fill in your details and jump straight to your dashboard."}
          </div>

          {step === "otp" ? (
            <Form layout="vertical" onFinish={handleVerifyOtp} autoComplete="off">
              <div className="FieldGroup">
                <Form.Item style={{ marginBottom: 0 }}>
                  <Input.OTP length={6} size="large" value={otp} onChange={setOtp} />
                </Form.Item>
              </div>

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={otpLoading}
                className="SubmitButton"
                style={{ width: "100%", marginTop: 24 }}
                icon={<ThunderboltOutlined />}
              >
                Verify &amp; Open Dashboard <ArrowRightOutlined />
              </Button>

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
                <Button type="link" style={{ padding: 0 }} onClick={handleBackToDetails}>
                  ← Back
                </Button>
                <Button type="link" style={{ padding: 0 }} loading={resending} onClick={handleResendOtp}>
                  Resend OTP
                </Button>
              </div>
            </Form>
          ) : (
          <Form layout="vertical" onFinish={handleSubmit} autoComplete="off">
             <div className="FieldRow FieldGroup">
              <Form.Item
                name="contactName"
                rules={[{ required: true, message: "Please enter your name!" }]}
                style={{ marginBottom: 0 }}
              >
                <TextField
                  label="Your Name"
                  required
                  placeholder="Contact person"
                  prefix={<UserOutlined />}
                  height={40}
                />
              </Form.Item>
              <Form.Item
                name="mobile"
                normalize={(value) => (value || "").replace(/\D/g, "").slice(0, 10)}
                rules={[
                  { required: true, message: "Please enter your mobile number!" },
                  { pattern: /^\d{10}$/, message: "Please enter a valid 10-digit mobile number!" },
                ]}
                style={{ marginBottom: 0 }}
              >
                <TextField
                  label="Mobile Number"
                  required
                  placeholder="10-digit"
                  prefix={<PhoneOutlined />}
                  maxLength={10}
                  height={40}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                    const allowedControlKeys = [
                      "Backspace",
                      "Delete",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "ArrowUp",
                      "ArrowDown",
                      "Home",
                      "End",
                    ];
                    if (e.ctrlKey || e.metaKey || allowedControlKeys.includes(e.key) || /^[0-9]$/.test(e.key)) {
                      return;
                    }
                    e.preventDefault();
                  }}
                />
              </Form.Item>
            </div>
            <div className="FieldGroup">
              <Form.Item
                name="email"
                rules={[
                  { required: true, type: "email", message: "Please enter a valid email!" },
                ]}
                style={{ marginBottom: 0 }}
              >
                <TextField
                  label="Hotel Email ID"
                  required
                  placeholder="invoices@yourhotel.com"
                  prefix={<MailOutlined />}
                  height={40}
                />
              </Form.Item>
            </div>

            <div className="FieldGroup">
              <Form.Item
                name="vendorName"
                rules={[{ required: true, message: "Please enter the vendor/property name!" }]}
                style={{ marginBottom: 0 }}
              >
                <TextField
                  label="Hotel Name"
                  required
                  placeholder="Legal entity name of your hotel"
                  prefix={<ShopOutlined />}
                  height={40}
                />
              </Form.Item>
            </div>

           

            <div className="FieldGroup">
              <Form.Item
                name="gstin"
                normalize={(value) =>
                  (value || "").toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 15)
                }
                rules={[
                  { required: true, message: "Please enter the GSTIN!" },
                  {
                    pattern: /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                    message: "Please enter a valid 15-character GSTIN!",
                  },
                ]}
                style={{ marginBottom: 0 }}
              >
                <TextField
                  label="Hotel GSTIN"
                  required
                  placeholder="e.g. 27AALCB4322J1ZM"
                  prefix={<IdcardOutlined />}
                  maxLength={15}
                  height={40}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                    const allowedControlKeys = [
                      "Backspace",
                      "Delete",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "ArrowUp",
                      "ArrowDown",
                      "Home",
                      "End",
                    ];
                    if (
                      e.ctrlKey ||
                      e.metaKey ||
                      allowedControlKeys.includes(e.key) ||
                      /^[0-9a-zA-Z]$/.test(e.key)
                    ) {
                      return;
                    }
                    e.preventDefault();
                  }}
                />
              </Form.Item>
            </div>

            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={isLoading}
              className="SubmitButton"
              style={{ width: "100%" }}
              icon={<ThunderboltOutlined />}
            >
              Send OTP to Verify <ArrowRightOutlined />
            </Button>
          </Form>
          )}

          {domain !== "app" && (
            <div className="FooterText">
              This portal is operated by finkraft.ai on behalf of {partnerName}.
              Not an official {partnerName} product.
            </div>
          )}
          </div>

          <div className="BrandLockup">
            <span
              className={
                domain === "app" || LOCAL_DOMAIN_LOGOS[domain]
                  ? "BrandLockupItem BrandLockupItem--large"
                  : "BrandLockupItem"
              }
            >
              {logoUrl && (
                <img
                  src={logoUrl}
                  alt=""
                  style={FOOTER_LOGO_HEIGHT[domain] ? { height: FOOTER_LOGO_HEIGHT[domain] } : undefined}
                />
              )}
            </span>
            {domain !== "app" && (
              <>
                <span
                  className="BrandLockupSeparator"
                  style={{
                    ...(FOOTER_SEPARATOR_MARGIN_LEFT[domain] !== undefined
                      ? { marginLeft: FOOTER_SEPARATOR_MARGIN_LEFT[domain] }
                      : {}),
                    ...(FOOTER_SEPARATOR_MARGIN_RIGHT[domain] !== undefined
                      ? { marginRight: FOOTER_SEPARATOR_MARGIN_RIGHT[domain] }
                      : {}),
                  }}
                >
                  ×
                </span>
                <img src={FinkraftLogo} alt="finkraft.ai" />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
